import { useEffect, useRef, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { makeStyles } from "@mui/styles";

import iuImg from "../../assets/iu.jpg";
import paulKimImg from "../../assets/paul-kim.jpg";
import imjaebeomImg from "../../assets/imjaebeom.png";
import tohSillyImg from "../../assets/toh-silly.png";
import yoonhaImg from "../../assets/yoonha.png";

const CATEGORIES = [
  {
    id: "music",
    label: "AI 음악",
    en: "AI Music",
    icon: "♪",
    tagline: "한 곡을 만드는 모든 단계, AI로 완성하세요.",
    description:
      "AI 커버부터 작곡, MR 제거까지. 음악 제작의 처음부터 끝까지 SoriSori가 함께합니다.",
    tools: [
      {
        ko: "AI 커버 제작",
        en: "AI Cover",
        desc: "원하는 가수의 목소리로 어떤 곡이든 커버하세요.",
        href: "https://app.sorisori.ai/generate-music",
      },
      {
        ko: "AI 음성 학습",
        en: "Voice Training",
        desc: "내 목소리, 또는 원하는 보이스 모델을 학습시켜 사용합니다.",
        href: "https://app.sorisori.ai/train-voice",
      },
      {
        ko: "AI 작곡",
        en: "AI Composer",
        desc: "한 줄 아이디어를 풀 트랙으로 변환합니다.",
        href: "https://app.sorisori.ai/generate-ttm",
      },
      {
        ko: "MR 제거",
        en: "Stem Splitter",
        desc: "보컬과 반주를 깔끔하게 분리합니다.",
        href: "https://app.sorisori.ai/vocal-extract",
      },
    ],
  },
  {
    id: "tts",
    label: "AI 문자음성 변환",
    en: "TTS",
    icon: "⌶",
    tagline: "글을 자연스러운 목소리로.",
    description:
      "오디오북, 내레이션, 캐릭터 보이스. 텍스트를 사람 같은 보이스로 변환합니다.",
    tools: [
      {
        ko: "AI 문자음성 변환",
        en: "Text → Voice",
        desc: "대본만 넣으면 감정과 톤까지 살아 있는 보이스로 출력됩니다.",
        href: "https://app.sorisori.ai/generate-tts",
      },
      {
        ko: "AI 문자음성 학습",
        en: "TTS Training",
        desc: "나만의 TTS 모델을 만들어 어디서든 사용하세요.",
        href: "https://app.sorisori.ai/tts-train-voice",
      },
    ],
  },
  {
    id: "image",
    label: "AI 이미지",
    en: "AI Image",
    icon: "◇",
    tagline: "아이디어를 시각으로.",
    description:
      "컨셉 이미지, 캐릭터, 일러스트까지. 텍스트나 레퍼런스로 시작하세요.",
    tools: [
      {
        ko: "AI 이미지 생성",
        en: "Image Gen",
        desc: "한 줄 프롬프트로 고해상도 이미지를 만듭니다.",
        href: "https://app.sorisori.ai/generate-tti",
      },
      {
        ko: "AI 이미지 학습",
        en: "Image Training",
        desc: "캐릭터/스타일을 학습시켜 일관된 비주얼을 유지합니다.",
        href: "https://app.sorisori.ai/tti-train-image",
      },
    ],
  },
  {
    id: "video",
    label: "AI 영상",
    en: "AI Video",
    icon: "▶",
    tagline: "영상에 새로운 얼굴을 입히세요.",
    description:
      "얼굴 합성, 더빙, 시뮬레이션까지. AI 비디오 워크플로우 한 곳에.",
    tools: [
      {
        ko: "페이스 스왑",
        en: "Face Swap",
        desc: "자연스러운 얼굴 교체로 콘텐츠 변형이 가능합니다.",
        href: "https://app.sorisori.ai/faceswap",
      },
    ],
  },
  {
    id: "editor",
    label: "영상 편집",
    en: "Video Editor",
    icon: "✂",
    tagline: "컷·자막·이펙트를 한 번에.",
    description:
      "브라우저에서 바로 편집하고 SoriSori 콘텐츠와 매끄럽게 연결됩니다.",
    tools: [
      {
        ko: "영상 편집",
        en: "Editor",
        desc: "드래그앤드롭으로 시퀀스를 짜고 자막·이펙트를 적용합니다.",
        href: "https://app.sorisori.ai/video-editor",
      },
    ],
  },
  {
    id: "community",
    label: "커뮤니티",
    en: "Community",
    icon: "◎",
    tagline: "보이스 모델 라이브러리.",
    description: "학습한 보이스 모델을 공유하고 사용하는 라이브러리.",
    tools: [
      {
        ko: "셀럽 보이스 라이브러리",
        en: "Celebrity Voice Library",
        desc: "검증된 보이스 모델을 한 번에 검색하고 적용합니다.",
      },
    ],
  },
];

const TOTAL_TOOLS = CATEGORIES.reduce((acc, c) => acc + c.tools.length, 0);

const MUSICIANS = [
  { name: "아이유 (데뷔초)", image: iuImg },
  { name: "폴킴", image: paulKimImg },
  { name: "임재범 ai", image: imjaebeomImg },
  { name: "Toh โต Silly Fools", image: tohSillyImg },
  { name: "윤하", image: yoonhaImg },
];

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: "#0a0a0a",
    padding: "96px 32px",
    color: "#fff",
    "@media (max-width:600px)": { padding: "64px 24px" },
  },
  inner: { maxWidth: 1280, margin: "0 auto" },
  topRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 64,
    alignItems: "flex-end",
    marginBottom: 56,
    "@media (max-width:900px)": { gridTemplateColumns: "1fr", gap: 24 },
  },
  miniLabel: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    color: "#555",
    marginBottom: 16,
  },
  headline: {
    fontSize: 44,
    fontWeight: 700,
    letterSpacing: "-1.8px",
    lineHeight: 1.1,
    color: "#fff",
    margin: 0,
    "@media (max-width:600px)": { fontSize: 32 },
  },
  description: {
    fontSize: 15,
    lineHeight: 1.65,
    color: "#888",
    maxWidth: 360,
    margin: 0,
    marginLeft: "auto",
    textAlign: "right",
    "@media (max-width:900px)": {
      marginLeft: 0,
      textAlign: "left",
    },
  },
  descBreakAfter: {
    "@media (max-width:600px)": {
      display: "block",
    },
  },
  tabs: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: 32,
    "@media (max-width:600px)": {
      flexWrap: "nowrap",
      overflowX: "auto",
      paddingBottom: 8,
      marginLeft: -24,
      marginRight: -24,
      paddingLeft: 24,
      paddingRight: 24,
      marginBottom: 12,
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": { display: "none" },
    },
  },
  scrollIndicator: {
    display: "none",
    "@media (max-width:600px)": {
      display: "block",
      width: 280,
      height: 3,
      background: "#1f1f1f",
      borderRadius: 999,
      margin: "0 auto 28px",
      position: "relative",
      overflow: "hidden",
    },
  },
  scrollIndicatorThumb: {
    position: "absolute",
    top: 0,
    bottom: 0,
    background: theme.palette.primary.main,
    borderRadius: 999,
    transition: "left 0.12s ease-out, width 0.12s ease-out",
  },
  tab: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 20px",
    borderRadius: 9999,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    border: "1px solid #252525",
    background: "#141414",
    color: "#bbb",
    textTransform: "none",
    transition: "all .2s",
    flexShrink: 0,
    "&:hover": { color: "#fff", borderColor: "#3a3a3a" },
  },
  tabActive: {
    background: theme.palette.primary.main,
    color: "#0a0a0a",
    borderColor: theme.palette.primary.main,
    "&:hover": {
      background: theme.palette.primary.main,
      color: "#0a0a0a",
      borderColor: theme.palette.primary.main,
    },
  },
  tabBadge: {
    fontSize: 11,
    fontWeight: 700,
    padding: "2px 7px",
    borderRadius: 9999,
    background: "rgba(0,0,0,0.15)",
  },
  tabBadgeInactive: {
    background: "#0a0a0a",
    color: "#666",
    border: "1px solid #252525",
  },
  body: {
    display: "grid",
    gridTemplateColumns: "380px 1fr",
    gap: 32,
    alignItems: "flex-start",
    "@media (max-width:900px)": { gridTemplateColumns: "1fr" },
  },
  hero: {
    background: "#0f0f0f",
    border: "1px solid #1f1f1f",
    borderRadius: 16,
    padding: 32,
    display: "flex",
    flexDirection: "column",
    gap: 18,
    position: "sticky",
    top: 100,
    "@media (max-width:900px)": { position: "static", padding: 24 },
  },
  heroIcon: {
    width: 56,
    height: 56,
    borderRadius: 14,
    background: "#181818",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 24,
    color: theme.palette.primary.main,
  },
  heroTitle: {
    fontSize: 28,
    fontWeight: 700,
    color: "#fff",
    lineHeight: 1.2,
    letterSpacing: "-0.5px",
    margin: 0,
  },
  heroEn: {
    fontSize: 12,
    color: "#666",
    fontWeight: 600,
    letterSpacing: "1.5px",
    textTransform: "uppercase",
  },
  heroTagline: {
    fontSize: 17,
    fontWeight: 500,
    color: "#fff",
    letterSpacing: "-0.3px",
    lineHeight: 1.45,
    margin: 0,
  },
  heroDesc: {
    fontSize: 14,
    color: "#888",
    lineHeight: 1.65,
    margin: 0,
  },
  heroCta: {
    color: theme.palette.primary.main,
    fontSize: 13,
    fontWeight: 700,
    textTransform: "none",
    padding: 0,
    minWidth: 0,
    alignSelf: "flex-start",
    "&:hover": { background: "transparent", opacity: 0.85 },
  },
  toolsArea: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  toolsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 12,
    "@media (max-width:600px)": { gridTemplateColumns: "1fr" },
  },
  toolsGridSingle: {
    gridTemplateColumns: "1fr",
  },
  musicianHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 4,
  },
  musicianHeaderTitle: {
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: "0.5px",
    color: "#888",
    textTransform: "uppercase",
  },
  musicianHeaderMore: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "1.5px",
    color: theme.palette.primary.main,
    cursor: "pointer",
    background: "transparent",
    border: "none",
    padding: 0,
    textDecoration: "none",
    "&:hover": { opacity: 0.85 },
  },
  musicianRow: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: 12,
    "@media (max-width:1100px)": { gridTemplateColumns: "repeat(4, 1fr)" },
    "@media (max-width:600px)": { gridTemplateColumns: "repeat(3, 1fr)" },
  },
  musicianCard: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    cursor: "pointer",
    minWidth: 0,
    textDecoration: "none",
    color: "inherit",
    "&:hover $musicianImage": { transform: "scale(1.02)" },
  },
  musicianImageWrap: {
    width: "100%",
    aspectRatio: "4 / 5",
    borderRadius: 14,
    overflow: "hidden",
    position: "relative",
    transition: "transform .2s",
  },
  musicianImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center top",
    display: "block",
    transition: "transform .25s",
  },
  musicianPlay: {
    position: "absolute",
    top: 10,
    right: 10,
    width: 26,
    height: 26,
    borderRadius: "50%",
    background: theme.palette.primary.main,
    color: "#0a0a0a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 10,
    fontWeight: 800,
    paddingLeft: 1,
  },
  musicianName: {
    fontSize: 13,
    color: "#cfcfcf",
    letterSpacing: "-0.2px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  toolCard: {
    background: "#151515",
    border: "1px solid #1f1f1f",
    borderRadius: 14,
    padding: "28px 28px 26px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    transition: "all .2s",
    cursor: "pointer",
    textDecoration: "none",
    color: "inherit",
    "&:hover": {
      background: "#1c1c1c",
      borderColor: "#333",
      "& $tryNow": { color: theme.palette.primary.main },
    },
    "@media (max-width:600px)": { padding: "22px 20px 20px" },
  },
  toolHeadRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  toolIndex: {
    fontSize: 11,
    fontWeight: 700,
    color: "#444",
    letterSpacing: "2px",
  },
  toolEn: {
    fontSize: 10,
    fontWeight: 700,
    background: "#0a0a0a",
    border: "1px solid #252525",
    padding: "3px 8px",
    borderRadius: 9999,
    color: "#666",
    letterSpacing: "0.5px",
  },
  toolTitle: {
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: "-0.5px",
    color: "#fff",
    lineHeight: 1.25,
    margin: 0,
  },
  toolDesc: {
    fontSize: 14,
    lineHeight: 1.6,
    color: "#888",
    flex: 1,
    margin: 0,
  },
  tryNow: {
    color: "#666",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "1.5px",
    transition: "color .2s",
    marginTop: 8,
  },
}));

export default function Products() {
  const classes = useStyles();
  const [active, setActive] = useState(CATEGORIES[0].id);
  const cat = CATEGORIES.find((c) => c.id === active);

  const tabsRef = useRef(null);
  const [tabsScroll, setTabsScroll] = useState({ ratio: 0, visible: 1 });

  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    const update = () => {
      const max = el.scrollWidth - el.clientWidth;
      const ratio = max > 0 ? el.scrollLeft / max : 0;
      const visible = el.scrollWidth > 0 ? el.clientWidth / el.scrollWidth : 1;
      setTabsScroll({ ratio, visible: Math.min(1, visible) });
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const tabsScrollable = tabsScroll.visible < 1;

  return (
    <Box component="section" className={classes.root}>
      <Box className={classes.inner}>
        <Box className={classes.topRow}>
          <Box>
            <Typography className={classes.miniLabel}>
              Products · {TOTAL_TOOLS} tools, 6 categories
            </Typography>
            <Typography component="h2" className={classes.headline}>
              모든 AI 창작 도구가
              <br />한 곳에.
            </Typography>
          </Box>
          <Typography className={classes.description}>
            카테고리를 선택해 SoriSori가 제공하는{" "}
            <Box component="span" className={classes.descBreakAfter}>
              도구를 살펴보세요.
            </Box>
          </Typography>
        </Box>

        <Box className={classes.tabs} ref={tabsRef}>
          {CATEGORIES.map((c) => {
            const isActive = c.id === active;
            return (
              <Button
                key={c.id}
                className={`${classes.tab} ${isActive ? classes.tabActive : ""}`}
                onClick={() => setActive(c.id)}
                disableRipple
                disableElevation
              >
                <span>{c.label}</span>
                <span
                  className={`${classes.tabBadge} ${
                    isActive ? "" : classes.tabBadgeInactive
                  }`}
                >
                  {String(c.tools.length).padStart(2, "0")}
                </span>
              </Button>
            );
          })}
        </Box>

        {tabsScrollable && (
          <Box className={classes.scrollIndicator} aria-hidden="true">
            <Box
              className={classes.scrollIndicatorThumb}
              style={{
                width: `${tabsScroll.visible * 100}%`,
                left: `${tabsScroll.ratio * (1 - tabsScroll.visible) * 100}%`,
              }}
            />
          </Box>
        )}

        <Box className={classes.body}>
          <Box className={classes.hero}>
            <Box className={classes.heroIcon}>{cat.icon}</Box>
            <Box>
              <Typography className={classes.heroEn}>{cat.en}</Typography>
              <Typography className={classes.heroTitle}>{cat.label}</Typography>
            </Box>
            <Typography className={classes.heroTagline}>
              {cat.tagline}
            </Typography>
            <Typography className={classes.heroDesc}>
              {cat.description}
            </Typography>
            <Button
              className={classes.heroCta}
              disableRipple
              {...(cat.id === "community"
                ? {
                    component: "a",
                    href: "https://app.sorisori.ai/community",
                    target: "_blank",
                    rel: "noopener noreferrer",
                  }
                : {})}
            >
              Explore all →
            </Button>
          </Box>

          <Box className={classes.toolsArea}>
            {cat.id === "community" ? (
              <>
                <Box className={classes.musicianHeader}>
                  <span className={classes.musicianHeaderTitle}>
                    Featured Voices
                  </span>
                  <Box
                    component="a"
                    href="https://app.sorisori.ai/community"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.musicianHeaderMore}
                  >
                    SEE ALL →
                  </Box>
                </Box>
                <Box className={classes.musicianRow}>
                  {MUSICIANS.slice(0, 5).map((m) => (
                    <Box
                      key={m.name}
                      component="a"
                      href="https://app.sorisori.ai/community"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.musicianCard}
                    >
                      <Box className={classes.musicianImageWrap}>
                        <img
                          src={m.image}
                          alt={m.name}
                          className={classes.musicianImage}
                        />
                        <Box className={classes.musicianPlay}>▶</Box>
                      </Box>
                      <Box className={classes.musicianName}>{m.name}</Box>
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <Box
                className={`${classes.toolsGrid} ${
                  cat.tools.length === 1 ? classes.toolsGridSingle : ""
                }`}
              >
                {cat.tools.map((tool, idx) => {
                  const linkProps = tool.href
                    ? {
                        component: "a",
                        href: tool.href,
                        target: "_blank",
                        rel: "noopener noreferrer",
                      }
                    : {};
                  return (
                    <Box
                      key={tool.ko}
                      className={classes.toolCard}
                      {...linkProps}
                    >
                      <Box className={classes.toolHeadRow}>
                        <span className={classes.toolIndex}>
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        <span className={classes.toolEn}>{tool.en}</span>
                      </Box>
                      <Typography className={classes.toolTitle}>
                        {tool.ko}
                      </Typography>
                      <Typography className={classes.toolDesc}>
                        {tool.desc}
                      </Typography>
                      <Box className={classes.tryNow}>TRY NOW →</Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
